import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, TransparentCard } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Mail, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { authAPI, RegisterRequest, ApiError } from "../services/api";
import PageWithNavbar from "@/components/PageWithNavbar";

export const RegistrationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Accept any state passed from payment/plan selection or login
  const redirectState = location.state ? { ...location.state } : undefined;
  const selectedPlan = location.state?.selectedPlan || null;
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
    referralCode: "", // Added referral code field
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms and conditions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    setErrors({});
    try {
      const registrationData: RegisterRequest = {
        firstname: formData.firstName.trim(),
        lastname: formData.lastName.trim(),
        username: formData.username,
        email: formData.email,
        password: formData.password,
        ...(formData.referralCode.trim() && { referralCode: formData.referralCode.trim() }) // Pass referral code if present
      };
      const response = await authAPI.register(registrationData);
      if (response) {
        setIsSuccess(true);
        // Store user data for immediate login after registration
        localStorage.setItem('doyenUser', response.username);
        // If we came from a payment/plan selection, preserve it for post-login redirect
        if (redirectState) {
          sessionStorage.setItem('postLoginRedirect', JSON.stringify(redirectState));
        }
        if(selectedPlan) 
        {
          goToCheckout();
        }
      } else {
        setErrors({ general: response || 'Registration failed. Please try again.' });
      }
    } catch (error) {
      const errorMessage = error instanceof ApiError 
        ? error.message 
        : 'Network error. Please try again.';
      setErrors({ general: errorMessage });
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // When user clicks "Already have an account? Login", preserve payment/plan state if present
  const goToLogin = () => {
    if (location.state) {
      navigate("/login", { state: { ...location.state } });
    } else {
      navigate("/login");
    }
  };

  const goToCheckout = () => {
    if (selectedPlan) {
      navigate("/checkout", { state: { selectedPlan } });
    } else {
      navigate("/checkout");
    }
  };

  return (
    <PageWithNavbar>
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Registration Form */}
        <div className="max-w-md mx-auto">          
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <h1 className="text-3xl font-bold">Create Account</h1>
            </div>
          </div>

          {/* Selected Plan Display */}
          {selectedPlan && (
            <Card className="mb-6 bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold flex items-center">
                      <CheckCircle className="h-4 w-4 text-primary mr-2" />
                      Selected Plan: {selectedPlan.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {selectedPlan.title === "Free" ? "Free forever" : `$${selectedPlan.price} one-time`}
                    </p>
                  </div>
                  <Badge variant={selectedPlan.title === "Premium" ? "default" : "secondary"}>
                    {selectedPlan.title}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          <TransparentCard>
            <CardHeader>
              <CardTitle>Sign Up</CardTitle>
              <CardDescription>
                Create your account to get started with our trading platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isSuccess ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Registration Successful!</h3>
                  <p className="text-muted-foreground mb-4">
                    {selectedPlan 
                      ? `Welcome to Doyen Trading ${selectedPlan.title} Plan! ${selectedPlan.title === 'Premium' ? 'Redirecting to checkout...' : 'You can start trading immediately!'}`
                      : 'You can now log in to access your account.'
                    }
                  </p>
                  <div className="animate-pulse text-sm text-muted-foreground">
                    Redirecting...
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {errors.general && (
                    <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-100 border border-red-300 rounded-md">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span>{errors.general}</span>
                    </div>
                  )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium mb-2">
                      First Name *
                    </label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange("firstName", e.target.value)}
                      className={errors.firstName ? "border-red-500" : ""}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium mb-2">
                      Last Name *
                    </label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange("lastName", e.target.value)}
                      className={errors.lastName ? "border-red-500" : ""}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                    )}
                  </div>
                </div>


                <div>
                  <label htmlFor="username" className="block text-sm font-medium mb-2">
                    Username *
                  </label>
                  <Input
                    id="username"
                    placeholder="yourusername"
                    value={formData.username}
                    onChange={(e) => handleInputChange("username", e.target.value)}
                    className={errors.username ? "border-red-500" : ""}
                  />
                  {errors.username && (
                    <p className="text-red-500 text-xs mt-1">{errors.username}</p>
                  )}
                </div>

                {/* Referral Code (Optional) */}
                <div>
                  <label htmlFor="referralCode" className="block text-sm font-medium mb-2">
                    Referral Code <span className="text-xs text-muted-foreground">(optional)</span>
                  </label>
                  <Input
                    id="referralCode"
                    placeholder="Enter referral code (if any)"
                    value={formData.referralCode}
                    onChange={(e) => handleInputChange("referralCode", e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Input
                      id="email"
                      type="email"
                      placeholder="john.doe@example.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className={`pl-10 ${errors.email ? "border-red-500" : ""}`}
                    />
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium mb-2">
                    Password *
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      className={`pl-10 pr-10 ${errors.password ? "border-red-500" : ""}`}
                    />
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                      className={`pl-10 pr-10 ${errors.confirmPassword ? "border-red-500" : ""}`}
                    />
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                  )}
                </div>

                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={(e) => handleInputChange("agreeToTerms", e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label htmlFor="agreeToTerms" className="text-sm text-muted-foreground">
                    I agree to the{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms of Service</a>{" "}
                    and{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>
                  </label>
                </div>
                {errors.agreeToTerms && (
                  <p className="text-red-500 text-xs">{errors.agreeToTerms}</p>
                )}

                {/* Crypto Checkout Notification */}
                {selectedPlan && (
                  <div className="max-w-md mx-auto mb-6">
                    <div className="bg-blue-100 border border-yellow-900 text-yellow-900 rounded-lg p-4 text-sm text-center">
                      Paying with cryptocurrency and want maximum privacy?<br/>
                      <Link
                        to="/crypto-checkout"
                        state={selectedPlan ? { selectedPlan } : undefined}
                        className="text-secondary font-medium underline hover:text-primary/80"
                      >Go directly to checkout</Link> and we'll autogenerate your username and password after payment is received.
                    </div>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={isLoading || !formData.agreeToTerms}
                >
                  {isLoading ? "Creating Account..." : "Create Account"}
                </Button>

                <div className="text-center pt-4">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <button
                      type="button"
                      className="text-primary hover:underline font-medium"
                      onClick={() => goToLogin()}>
                      Sign in here
                    </button>
                  </p>
                </div>
              </form>
              )}
            </CardContent>
          </TransparentCard>
        </div>
      </div>
    </div>
    </PageWithNavbar>
  );
};

export default RegistrationPage;
